<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <div
    v-for="(item, index) in slotProps.items"
    :key="index"
    class="list-item-wrapper"
    role="listitem"
  >
    <div class="list-item-container">
      <div
        class="list-item-row"
        :class="{
          'compact-mode': isPreviewPanelVisible || isSmallScreen,
          'has-extra-column': hasExtraColumn,
        }"
      >
        <!-- Checkbox Column -->
        <div class="col-checkbox" data-tour="item-checkbox">
          <PrimeCheckBox
            :id="item.id"
            v-model="selectedNodesCopy"
            name="option"
            :value="item"
          />
        </div>

        <!-- Name Column -->
        <div class="col-name" @click="nodeClicked(item)">
          <i
            v-if="item.isLocked"
            class="lock-icon ri-lock-line"
            aria-hidden="true"
            v-tooltip="alfrescoFileService().tooltipLockContent(item)"
          ></i>
          <PrimeButton
            v-else-if="item.permissions?.isInheritanceEnabled === false"
            type="button"
            icon="ri-shield-user-line"
            severity="warn"
            text
            rounded
            size="small"
            :aria-label="$t('restrictedPermissions')"
            v-tooltip="$t('restrictedPermissions')"
            @click.stop="$emit('open-members', item)"
          />
          <span v-if="item.isFile" data-tour="thumbnail">
            <PristyIcon
              v-tooltip="loadIconLabel(item)"
              class="file-icon cursor-pointer"
              :svg-file-name="loadIcon(item)"
              @click.stop="clickIcon(item)"
            />
          </span>
          <i v-else class="folder-icon ri-folder-2-line" aria-hidden="true" />
          <router-link
            v-if="item.isFile"
            v-tooltip.top="item.name"
            :to="collaboraService.getRoute(item)"
            class="item-name"
            :class="{ underline: isCurrentNodeDisplayed(item) }"
          >
            {{ item.name }}
          </router-link>
          <span
            v-else
            v-tooltip.top="item.name"
            class="item-name"
            @click.stop="nodeClicked(item)"
          >
            {{ item.name }}
          </span>
          <span
            v-if="item.properties && item.properties['cm:title']"
            v-tooltip.bottom="item.properties['cm:title']"
            class="item-title"
            @click.stop="openPreviewPanel(item)"
          >
            - {{ item.properties["cm:title"] }}
          </span>
        </div>

        <!-- Extra slot (e.g. publication status) -->
        <div v-if="hasExtraColumn" class="col-extra">
          <slot name="list-item-extra" :item="item" />
        </div>

        <!-- Date Column -->
        <div v-if="!(isPreviewPanelVisible || isSmallScreen)" class="col-date">
          {{ toLocaleDateString(item.modifiedAt) }}
        </div>

        <!-- Actions Column -->
        <div class="col-actions">
          <template v-if="isPreviewPanelVisible || isSmallScreen">
            <PrimeButton
              v-tooltip="$t('moreActions')"
              :aria-label="$t('moreActions')"
              type="button"
              size="small"
              icon="ri-more-2-line"
              severity="secondary"
              @click="toggleFileActionMenu($event, item)"
            />
          </template>
          <template v-else>
            <PrimeButton
              v-tooltip.top="$t(`open`)"
              :aria-label="$t('open')"
              icon="ri-external-link-line"
              severity="secondary"
              size="small"
              @click="openInNewTab(item)"
            />
            <PrimeButton
              v-if="item.isFile"
              v-tooltip.top="$t(`publish`)"
              :aria-label="$t('publish')"
              icon="ri-links-line"
              severity="warning"
              size="small"
              @click="openShareLink(item)"
            />
            <PrimeButton
              v-if="!isInUserHome"
              v-tooltip.top="$t(`add/removeFavorite`)"
              :aria-label="$t('add/removeFavorite')"
              :icon="item.isFavorite ? 'ri-star-fill' : 'ri-star-line'"
              severity="primary"
              size="small"
              @click="toggleFavorite(item)"
            />
            <PrimeButton
              v-tooltip.top="$t(`moreActions`)"
              :aria-label="$t('moreActions')"
              icon="ri-more-2-line"
              severity="danger"
              size="small"
              @click="toggleFileActionMenu($event, item)"
            />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import PristyIcon from "../PristyIcon.vue";
import Tooltip from "primevue/tooltip";
import Button from "primevue/button";
import CheckBox from "primevue/checkbox";
import formatService from "@/services/FormatService.js";
import iconeService from "@/services/IconeService.js";

import alfrescoFileService from "@/services/AlfrescoFileService.js";

export default {
  name: "ContentListView",
  components: {
    PristyIcon,
    PrimeButton: Button,
    PrimeCheckBox: CheckBox,
  },
  directives: {
    tooltip: Tooltip,
  },
  inject: ["collaboraService", "isCurrentNodeDisplayed"],
  props: {
    slotProps: {
      type: Object,
      default: () => ({}),
    },
    selectedNodes: {
      type: Array,
      default: () => [],
    },
    isPreviewPanelVisible: {
      type: Boolean,
      default: false,
    },
    isSmallScreen: {
      type: Boolean,
      default: false,
    },
    isInUserHome: {
      type: Boolean,
      default: false,
    },
    hasExtraColumn: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    "update:selectedNodes",
    "click-icon",
    "open-preview-panel",
    "open-sharelink",
    "toggle-favorite",
    "toggle-file-action-menu",
    "open-in-new-tab",
    "node-clicked",
    "open-members",
  ],
  computed: {
    selectedNodesCopy: {
      get() {
        return this.selectedNodes;
      },
      set(value) {
        this.$emit("update:selectedNodes", value);
      },
    },
  },
  methods: {
    alfrescoFileService() {
      return alfrescoFileService;
    },
    toLocaleDateString(datestr) {
      return formatService.toLocaleDateString(datestr);
    },
    loadIcon(item) {
      return iconeService.getIcon(item);
    },
    loadIconLabel(item) {
      return iconeService.getIconLabel(item);
    },
    clickIcon(node) {
      this.$emit("click-icon", node);
    },
    openPreviewPanel(node) {
      this.$emit("open-preview-panel", node);
    },
    openShareLink(node) {
      this.$emit("open-sharelink", node);
    },
    toggleFavorite(node) {
      this.$emit("toggle-favorite", node);
    },
    toggleFileActionMenu(event, node) {
      this.$emit("toggle-file-action-menu", event, node);
    },
    openInNewTab(node) {
      this.$emit("open-in-new-tab", node);
    },
    nodeClicked(node) {
      this.$emit("node-clicked", node);
    },
  },
};
</script>
<style lang="scss" scoped>
.list-item-wrapper {
  width: 100%;
}

.list-item-container {
  border-bottom: 1px solid var(--p-surface-border);

  &:hover {
    background-color: var(--p-content-hover-background);
  }
}

.list-item-wrapper:first-child .list-item-container {
  border-top: 1px solid var(--p-surface-border);
}

.list-item-row {
  display: grid;
  grid-template-columns: 2.5rem 1fr 7rem 9rem;
  align-items: center;
  padding: 0.5rem 0.75rem;
  gap: 0.75rem;

  &.has-extra-column {
    grid-template-columns: 2.5rem 1fr 8rem 7rem 9rem;
  }

  &.compact-mode {
    grid-template-columns: 2.5rem 1fr 3rem;

    &.has-extra-column {
      grid-template-columns: 2.5rem 1fr 3rem;
    }
  }
}

/* Checkbox Column */
.col-checkbox {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Name Column */
.col-name {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  overflow: hidden;
  cursor: pointer;
}

.lock-icon {
  color: var(--p-orange-500);
  flex-shrink: 0;
}

.file-icon {
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
}

.folder-icon {
  font-size: 1.25rem;
  color: var(--p-primary-color);
  flex-shrink: 0;
}

.item-name {
  font-weight: 500;
  color: var(--p-text-color);
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    color: var(--p-primary-color);
    text-decoration: underline;
  }

  &.underline {
    text-decoration: underline;
    color: var(--p-primary-color);
  }
}

.item-title {
  color: var(--p-text-muted-color);
  font-size: 0.875rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-shrink: 1;
}

/* Extra Column */
.col-extra {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Date Column */
.col-date {
  color: var(--p-text-muted-color);
  font-size: 0.875rem;
  text-align: center;
  white-space: nowrap;
}

/* Actions Column */
.col-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.25rem;

  :deep(.p-button) {
    padding: 0.4rem;
  }
}

.cursor-pointer {
  cursor: pointer;
}
</style>
